import React, { useState, useEffect } from 'react';
import '../styles/pages/AboutPage.css';
import { motion } from 'framer-motion';
import { TeamCardSkeleton } from '../components/SkeletonLoader';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import PageWrapper from '../components/PageWrapper';
import api from '../services/api';
import SafeImage from '../components/SafeImage';
import useCardGlow from '../hooks/useCardGlow';

const AboutPage = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initialize card glow effect
  useCardGlow();

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        const response = await api.getTeamMembers();
        if (response.success) {
          setTeamMembers(response.data);
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  const handleCardClick = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  return (
    <PageWrapper>
      <SEO
        title="About Us | Supernova Lab"
        description="Learn about Supernova Lab - Barbados' national engine for public-interest experimentation. Discover our mission to shape, test, and share innovative solutions."
        keywords="about supernova lab, innovation lab, public experimentation, Barbados innovation, government innovation"
        ogImage="https://supernovalab.bb/assets/images/og-about.jpg"
        canonicalUrl="https://supernovalab.bb/about-us"
      />
      <main className="about-page">
        {/* Who We Are Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <section className="who-we-are-section ">
            <div className="container px-3 px-md-4">
              <h1 className="section-title">
                Who <span className="animated-title-primary">We Are.</span>
              </h1>
              <p className="section-description">
                Supernova Lab is Barbados' national engine for public-interest experimentation.
                We operate the island as a mission-aligned proving ground where frontier solutions
                are exposed to real systems, real constraints, and real accountability.
              </p>
              <p className="section-description mt-4">
                By combining foresight, R&D infrastructure, multimedia and storytelling, we help
                governments, businesses and mission-driven teams test what works, surface what doesn't,
                and turn proven breakthroughs into shared public assets.
              </p>
            </div>
          </section>
        </motion.section>

        {/* Our O/S Section */}
        <motion.section
          className="our-os-section py-5"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="container px-3 px-md-4">
            <h2 className="our-os-title mb-2">
              Our <span className="text-gradient-our-os">O/S</span>
            </h2>
            <p className="our-os-subtitle mb-5">
              The operating system that powers Barbados' mission-aligned proving ground.
            </p>

            <div className="row g-4">
              {/* Shape Card - GREEN GLOW */}
              <div className="col-lg-4">
                <div className="os-card shape-card" data-glow data-glow-color="green">
                  <div className="os-card-content">
                    <div className="os-badge shape-badge">Shape</div>
                    <h5 className="os-label-shape">ARC Academy</h5>
                    <h3 className="os-title">Build People</h3>
                    <p className="os-subtitle">Internal Function</p>
                    <h4 className="os-function">Learning & Fellowship</h4>
                    <p className="os-description">
                      Fellowships, mastery sessions and workshops delivered via our ARC Academy
                      that arms mission-driven people with the capacity for experimentation
                      alongside mission-driven people in every sector.
                    </p>
                    <Link to="/experimentation-academy">
                      <button className="mt-3 see-more-btn">See More</button>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Test Card - PURPLE GLOW */}
              <div className="col-lg-4">
                <div className="os-card test-card" data-glow data-glow-color="purple">
                  <div className="os-card-content">
                    <div className="os-badge test-badge">Test</div>
                    <h5 className="os-label-test">Experimentation Zone</h5>
                    <h3 className="os-title">Build Systems</h3>
                    <p className="os-subtitle">Internal Function</p>
                    <h4 className="os-function">Applied Experimentation</h4>
                    <p className="os-description">
                      Barbados is a nationwide testbed for new ideas. Through our Readiness Lab,
                      agile pilots, rapid-cycle experiments, impact assessments and toolkits to
                      help teams design and run experiments that align with public goals.
                    </p>
                    <Link to="/experimentation-zone">
                      <button className="mt-3 see-more-btn">See More</button>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Share Card - ORANGE GLOW */}
              <div className="col-lg-4">
                <div className="os-card share-card" data-glow data-glow-color="orange">
                  <div className="os-card-content">
                    <div className="os-badge share-badge">Share</div>
                    <h5 className="os-label-share">Lab Stories</h5>
                    <h3 className="os-title">Build Belief</h3>
                    <p className="os-subtitle">Internal Function</p>
                    <h4 className="os-function">Media & Engagement</h4>
                    <p className="os-description">
                      Our R&D Media Hub, podcast and video platforms share the stories behind our
                      experiments in ways that inform, inspire, build citizen buy-in, surface
                      users, valued and adopted across the public sphere.
                    </p>
                    <Link to="/lab-stories">
                      <button className="mt-3 see-more-btn">See More</button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Our Team Section */}
        <section className="our-team-section">
          <div className="container px-3 px-md-4">
            <h2 className="our-team-section-tile mb-2">
              Our <span className="text-gradient-our-team">Team</span>
            </h2>
            <p className="section-subtitle mb-5">
              Our success is driven by the expertise and vision of our dedicated team. With a shared
              commitment to innovation, integrity, and exceptional service.
            </p>

            <div className="row g-4">
              {loading ? (
                Array(6).fill(0).map((_, index) => (
                  <div key={index} className="col-lg-4 col-md-6">
                    <TeamCardSkeleton />
                  </div>
                ))
              ) : (
                teamMembers.map((member, index) => (
                  <div key={member.id} className="col-lg-4 col-md-6">
                    <div
                      className={`team-card ${expandedCard === index ? 'expanded' : ''}`}
                      onClick={() => handleCardClick(index)}
                    >
                      <div className="team-card-front">
                        <SafeImage
                          src={member.image}
                          alt={member.name}
                          className="team-image"
                          placeholderText="{member.name}"
                        />
                        <div className="team-overlay">
                          <div className="team-info">
                            <h4 className="team-name">{member.name}</h4>
                            <p className="team-role">{member.role}</p>
                          </div>
                          <button className="team-arrow" aria-label="View details">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M7 17L17 7M17 7H7M17 7V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className="team-card-back">
                        <div className="team-bio-content">
                          <div className="team-bio-header">
                            <div>
                              <h4 className="team-bio-name">{member.name}</h4>
                              <p className="team-bio-role">{member.role}</p>
                            </div>
                            <a
                              href={member.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="linkedin-icon"
                              onClick={(e) => e.stopPropagation()}
                              aria-label="LinkedIn profile"
                            >
                              <i className="fab fa-linkedin-in"></i>
                            </a>
                          </div>
                          <p className="team-bio-text">{member.bio}</p>
                          <button className="team-back-arrow" aria-label="Go back">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M17 7L7 17M7 17H17M7 17V7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      </main>
   </PageWrapper>
  );
};

export default AboutPage;